import React, { useCallback, useRef, useState, useMemo, useEffect } from 'react';
import classNames from 'classnames';
import { Container } from '@johnlewispartnership/wtr-ingredients/dist/foundations/grid';
import { useMediaQuery } from 'react-responsive';
import { useDispatch, useSelector } from 'react-redux';
import { useAuthentication } from '@johnlewispartnership/wtr-website-authentication/dist/context';
import { BREAKPOINTS } from 'constants/grid';
import HeaderLogo from 'components/HeaderLogo';
import { useScrolled } from 'hooks/use-scrolled';
import SearchForm from 'components/Search/SearchForm';
import SkipLink from 'components/SkipLink';
import { stickyHeaderOffset } from 'constants/siteHeader';
import { thirtyDaysInMilliseconds } from 'constants/cookies';
import { usePreloadedData } from 'contexts/PreloadedData';
import contentService from 'services/content';
import useMonetateReporter from 'hooks/use-monetate-reporter';
import { useSlotContext } from 'contexts/Slot';
import { deleteCookie, setCookie } from 'utils/cookie';
import SitePinBar from 'components/SitePinBar';
import AppBanner from 'components/AppBanner';
import { useMinSpend } from 'hooks/use-min-spend';
import { useHeaderHeight } from 'hooks/use-header-height';
import {
  OPEN_MOBILE_OVERLAY_WINDOW_EVENT,
  OPEN_MOBILE_OVERLAY_WINDOW_CLEAR_EVENT,
} from 'eventbus/header';
import MobileOverlay from 'components/SiteHeader/MobileOverlay';
import setClearValueAction from 'redux/modules/page/actions/set-clear-value';
import setMobileOverlayAction from 'redux/modules/page/actions/set-mobile-overlay';
import SlideOutNav from './SlideOutNav';
import styles from './MobileSiteHeader.module.scss';
import LinksBar from './LinksBar';

const fetchContent = async (experienceFragmentKey, hasTokenSession) =>
  contentService.getContent(experienceFragmentKey, hasTokenSession);

const MobileSiteHeader = () => {
  const { state: authState } = useAuthentication();
  const { showMinSpend } = useMinSpend();

  useMonetateReporter();

  const [isSeasonalMenuOpen, setIsSeasonalMenuOpen] = useState(false);

  const dispatch = useDispatch();
  const isOverlayOpen = useSelector(state => state.page.isMobileOverlayOpen);
  const clearValue = useSelector(state => state.page.isValueClear);
  const setClearValue = useCallback(
    isValueClear => dispatch(setClearValueAction(isValueClear)),
    [dispatch],
  );

  const openMobileOverlay = useCallback(() => {
    dispatch(setMobileOverlayAction(true));
    setClearValue(false);
  }, [dispatch, setClearValue]);

  const toggleOverlayWithClear = useCallback(() => {
    dispatch(setMobileOverlayAction(!isOverlayOpen));
    setClearValue(true);
  }, [isOverlayOpen, setClearValue, dispatch]);

  useEffect(() => {
    window.addEventListener(OPEN_MOBILE_OVERLAY_WINDOW_EVENT, openMobileOverlay);
    window.addEventListener(OPEN_MOBILE_OVERLAY_WINDOW_CLEAR_EVENT, toggleOverlayWithClear);

    return () => {
      window.removeEventListener(OPEN_MOBILE_OVERLAY_WINDOW_EVENT, openMobileOverlay);
      window.removeEventListener(OPEN_MOBILE_OVERLAY_WINDOW_CLEAR_EVENT, toggleOverlayWithClear);
    };
  }, [dispatch, isOverlayOpen, setClearValue, openMobileOverlay, toggleOverlayWithClear]);

  const ref = useRef();
  useHeaderHeight(ref);
  const { scrolled } = useScrolled(stickyHeaderOffset);

  const isMobile = useMediaQuery({ maxWidth: BREAKPOINTS.md - 1 });
  const isXSMobile = useMediaQuery({ maxWidth: BREAKPOINTS.xs_sm - 1 });
  const isXXSMobile = useMediaQuery({ maxWidth: BREAKPOINTS.xs_xs - 1 });

  const viewport = useMemo(() => {
    if (isXXSMobile) return 'xxs-mobile';
    if (isXSMobile) return 'xs-mobile';
    return 'mobile';
  }, [isXXSMobile, isXSMobile]);

  useEffect(() => {
    if (authState.initialised) {
      if (authState.isLoggedIn) {
        // Set customerId cookie for server-side reporting and third-party integrations
        setCookie('customerId', authState.customerId, {
          secure: true,
          maxAge: thirtyDaysInMilliseconds,
        });
      } else {
        deleteCookie('customerId');

        if (authState.hasTokenSession) {
          // Server-side rendered the logged-in header but now we've done the auth flow
          // client-side and they're not actually logged in, so it'll re-render logged-out.
          // This is expected e.g. when token expires, but we log it so we can track/analyse.
          const errorToSendToApm = new Error('Re-rendering header from logged-in to logged-out');
          window?.apm?.captureError(
            Object.assign(errorToSendToApm, {
              event: 'header_flicker',
              authError: authState.error?.message,
            }),
          );
        }
      }
    }
  }, [
    authState.customerId,
    authState.error,
    authState.hasTokenSession,
    authState.initialised,
    authState.isLoggedIn,
  ]);

  const { preloadedData, setPreloadedData } = usePreloadedData();

  useEffect(() => {
    if (
      typeof preloadedData?.middleMegaMenu === 'undefined' ||
      typeof preloadedData?.seasonalMenu === 'undefined'
    ) {
      const middleMegaMenuContent =
        typeof preloadedData?.middleMegaMenu === 'undefined'
          ? fetchContent('menu/middle-mega-menu/master', authState.hasTokenSession)
          : preloadedData.middleMegaMenu;

      const seasonalMenuContent =
        typeof preloadedData?.seasonalMenu === 'undefined'
          ? fetchContent(
              'menu/seasonal-menu/seasonal-menu-content/master',
              authState.hasTokenSession,
            )
          : preloadedData.seasonalMenu;

      // deepcode ignore PromiseNotCaughtGeneral - SeviceClients have built-in error handling and always resolves
      Promise.allSettled([middleMegaMenuContent, seasonalMenuContent]).then(
        ([middleMegaMenu, seasonalMenu]) => {
          setPreloadedData({
            middleMegaMenu: middleMegaMenu.value,
            seasonalMenu: seasonalMenu.value,
          });
        },
      );
    }
  }, [preloadedData, setPreloadedData, authState.hasTokenSession]);

  const shouldRenderLoggedInVersion =
    (authState.hasTokenSession && !authState.initialised) ||
    (authState.initialised && authState.isLoggedIn);

  const {
    slotContext: { loading: slotLoading, booked: slotBooked, details: slotDetails },
  } = useSlotContext();

  const toggleSeasonalMenu = useCallback(
    () => setIsSeasonalMenuOpen(!isSeasonalMenuOpen),
    [isSeasonalMenuOpen],
  );

  return (
    <>
      {/*
        This style tag avoids a flash of unstyled content.
        These styles are overwritten by the CSS file once loaded.
        See WPIP-72426.
      */}
      <style>
        {`
          .common-mfe-header {
            height: 50px;
            visibility: hidden;

            @media (min-width: 992px) {
              height: 159px;
            }
          }
        `}
      </style>
      <div className={`${styles.header} common-mfe-header`} data-testid="mobile-site-header">
        <SkipLink />
        <AppBanner />
        <div className={styles.mobile}>
          <div
            className={classNames(styles.mobileHeader, {
              [styles.scrolled]: scrolled,
              [styles.hasMinSpend]: showMinSpend,
            })}
            data-testid="MobileHeader"
            ref={ref}
          >
            <Container className={classNames(styles.container)}>
              <div
                className={classNames(styles.mobileMenu, { [styles.hasMinSpend]: showMinSpend })}
              >
                <SlideOutNav
                  isSlotBooked={slotBooked}
                  slotDetails={slotDetails}
                  slotLoading={slotLoading}
                  isLoggedIn={shouldRenderLoggedInVersion}
                />
              </div>
              <div
                className={classNames(styles.mobileLogo, { [styles.hasMinSpend]: showMinSpend })}
              >
                <HeaderLogo small staticRender={false} />
              </div>
              <div
                className={classNames(styles.inner, {
                  [styles.isScrolled]: scrolled,
                  [styles.hasMinSpend]: showMinSpend,
                })}
                data-testid="inner"
              >
                <div className={styles.search} data-testid="search">
                  <SearchForm
                    historyClassName={isMobile && styles.mobileSearchHistoryContainer}
                    placeholder="Search groceries..."
                  />
                </div>
                <div
                  className={classNames(styles.links, {
                    [styles.hasMinSpend]: showMinSpend,
                  })}
                  data-testid="links"
                >
                  <LinksBar
                    viewport={viewport}
                    showContactAddressNotPresentNotification={false}
                    currentPageIsGroceriesHomePage={false}
                    isLoggedIn={shouldRenderLoggedInVersion}
                    onToggleSeasonalMenu={toggleSeasonalMenu}
                    isSeasonalMenuOpen={isSeasonalMenuOpen}
                    scrolled={scrolled}
                    showMinSpend={showMinSpend}
                  />
                </div>
              </div>
            </Container>
          </div>
        </div>
        {isOverlayOpen && <MobileOverlay clearValue={clearValue} setClearValue={setClearValue} />}
        <SitePinBar />
      </div>
    </>
  );
};

export { MobileSiteHeader };
